(() => {
  'use strict';

  const PRIMARY_STORAGE_KEY = 'wishlistItem';
  const FALLBACK_STORAGE_KEY = 'wishlist';
  const CHANGE_EVENT = 'wishlist:change';
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/i;
  const FETCH_CONCURRENCY = 4;
  const DEFAULT_TEXT = {
    linkLabel: 'Wish lists',
    addLabel: 'Add to wishlist',
    savedLabel: 'Saved to wishlist',
    removeLabel: 'Remove',
    addedMessage: 'Added to your wishlist.',
    removedMessage: 'Removed from your wishlist.',
    inStock: 'In stock',
    soldOut: 'Sold out',
    addToCart: 'Add to cart',
    chooseOptions: 'Choose options',
    viewProduct: 'View product',
    oneSavedItem: '1 saved item',
    savedItems: '[count] saved items',
    oneUnavailable: 'One saved product is no longer available.',
    unavailableItems: '[count] saved products are no longer available.'
  };

  let handles = readStoredHandles();
  let mutationSyncQueued = false;
  let wishlistPageRenderToken = 0;

  function text(key) {
    const config = document.querySelector('[data-wishlist-config]');
    return (config?.dataset?.[key] || DEFAULT_TEXT[key] || '').trim();
  }

  function countText(count) {
    return count === 1 ? text('oneSavedItem') : text('savedItems').replace('[count]', String(count));
  }

  function safeStorageGet(key) {
    try {
      return { available: true, value: window.localStorage.getItem(key) };
    } catch (error) {
      return { available: false, value: null };
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function decodeHandle(value) {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  function normalizeHandle(value) {
    if (typeof value !== 'string') return null;

    let candidate = value.trim();
    if (!candidate) return null;

    const productMatch = candidate.match(/(?:^|\/)products\/([^/?#]+)/i);
    if (productMatch) candidate = productMatch[1];

    candidate = decodeHandle(candidate).trim().replace(/^\/+|\/+$/g, '');
    return HANDLE_PATTERN.test(candidate) ? candidate.toLowerCase() : null;
  }

  function handleFromEntry(entry) {
    if (typeof entry === 'string') return normalizeHandle(entry);
    if (!entry || typeof entry !== 'object') return null;

    const candidates = [
      entry.handle,
      entry.product_handle,
      entry.productHandle,
      entry.url,
      entry.productUrl,
      entry.product_url,
      entry.product && entry.product.handle,
      entry.product && entry.product.url
    ];

    for (const candidate of candidates) {
      const normalized = normalizeHandle(candidate);
      if (normalized) return normalized;
    }

    return null;
  }

  function normalizeEntries(entries) {
    const normalized = [];
    const seen = new Set();

    for (const entry of entries) {
      const handle = handleFromEntry(entry);
      if (!handle || seen.has(handle)) continue;
      seen.add(handle);
      normalized.push(handle);
    }

    return normalized;
  }

  function parsePrimaryPayload(rawValue) {
    if (rawValue === null) return null;

    try {
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? normalizeEntries(parsed) : null;
    } catch (error) {
      return null;
    }
  }

  function parseFallbackPayload(rawValue) {
    if (rawValue === null) return null;

    let parsed;
    try {
      parsed = JSON.parse(rawValue);
    } catch (error) {
      parsed = rawValue;
    }

    if (Array.isArray(parsed)) return normalizeEntries(parsed);

    if (parsed && typeof parsed === 'object') {
      const candidates = [parsed.items, parsed.products, parsed.wishlist, parsed.handles];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return normalizeEntries(candidate);
      }

      const single = handleFromEntry(parsed);
      return single ? [single] : [];
    }

    if (typeof parsed === 'string') {
      const direct = normalizeHandle(parsed);
      if (direct) return [direct];
      return normalizeEntries(parsed.split(',').map((item) => item.trim()));
    }

    return [];
  }

  function readStoredHandles() {
    const primary = safeStorageGet(PRIMARY_STORAGE_KEY);
    if (primary.available && primary.value !== null) {
      const parsedPrimary = parsePrimaryPayload(primary.value);
      if (parsedPrimary !== null) return parsedPrimary;
    }

    const fallback = safeStorageGet(FALLBACK_STORAGE_KEY);
    if (fallback.available && fallback.value !== null) {
      const parsedFallback = parseFallbackPayload(fallback.value);
      if (parsedFallback !== null) return parsedFallback;
    }

    return [];
  }

  function persistHandles(nextHandles) {
    handles = normalizeEntries(nextHandles);
    safeStorageSet(PRIMARY_STORAGE_KEY, JSON.stringify(handles));
    return handles;
  }

  function wishlistContains(handle) {
    return handles.includes(handle);
  }

  function productTitleForControl(control) {
    return (control.dataset.productTitle || '').trim() || 'this product';
  }

  function updateControlState(control) {
    const handle = normalizeHandle(control.dataset.productHandle || '');
    if (!handle) return;

    const isSaved = wishlistContains(handle);
    const title = productTitleForControl(control);
    control.classList.toggle('is-active', isSaved);
    control.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    control.setAttribute('aria-label', `${isSaved ? text('removeLabel') : text('addLabel')}: ${title}`);

    const label = control.querySelector('[data-wishlist-label]');
    if (label) label.textContent = isSaved ? text('savedLabel') : text('addLabel');
  }

  function syncControls(root = document) {
    if (root instanceof Element && root.matches('[data-wishlist-toggle]')) updateControlState(root);
    root.querySelectorAll?.('[data-wishlist-toggle]').forEach(updateControlState);
  }

  function ensureStatusRegion() {
    let region = document.querySelector('[data-wishlist-global-status]');
    if (region) return region;

    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.dataset.wishlistGlobalStatus = '';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.append(region);
    return region;
  }

  function announce(message) {
    const region = ensureStatusRegion();
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  function dispatchChange(handle, action) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      detail: { handles: [...handles], handle, action }
    }));
  }

  function toggleWishlist(control) {
    const handle = normalizeHandle(control.dataset.productHandle || '');
    if (!handle) return;

    const title = productTitleForControl(control);
    const currentlySaved = wishlistContains(handle);
    const nextHandles = currentlySaved
      ? handles.filter((savedHandle) => savedHandle !== handle)
      : [...handles, handle];

    persistHandles(nextHandles);
    syncControls();

    const action = currentlySaved ? 'remove' : 'add';
    announce(`${title}: ${currentlySaved ? text('removedMessage') : text('addedMessage')}`);
    dispatchChange(handle, action);
  }

  function rootUrl(path = '') {
    const configuredRoot = window.Shopify?.routes?.root || '/';
    const normalizedRoot = configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
    return `${normalizedRoot}${String(path).replace(/^\/+/, '')}`;
  }

  function createHeartIcon() {
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const path = document.createElementNS(namespace, 'path');
    path.setAttribute('d', 'M12 20.25s-7.5-4.7-7.5-10.45A4.3 4.3 0 0 1 12 6.85a4.3 4.3 0 0 1 7.5 2.95c0 5.75-7.5 10.45-7.5 10.45Z');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.6');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
  }

  function createHeaderWishlistLink(className) {
    const link = document.createElement('a');
    link.className = className;
    link.href = rootUrl('pages/wish-list');
    link.dataset.wishlistHeaderLink = '';
    link.append(createHeartIcon());

    const label = document.createElement('span');
    label.textContent = text('linkLabel');
    link.append(label);
    return link;
  }

  function ensureHeaderLinks(root = document) {
    const header = root.querySelector?.('[data-site-header]') || (root.matches?.('[data-site-header]') ? root : null);
    if (!header) return;

    const actions = header.querySelector('.site-header__actions');
    if (actions && !actions.querySelector('[data-wishlist-header-link]')) {
      const desktopLink = createHeaderWishlistLink('site-header__utility site-header__utility--desktop wishlist-header-link');
      const accountLink = actions.querySelector('.site-header__utility--desktop');
      actions.insertBefore(desktopLink, accountLink || actions.querySelector('.site-header__utility--cart') || null);
    }

    let mobileUtilities = header.querySelector('.mobile-nav-drawer__utilities');
    if (!mobileUtilities) {
      const panel = header.querySelector('.mobile-nav-drawer__panel');
      if (panel) {
        mobileUtilities = document.createElement('div');
        mobileUtilities.className = 'mobile-nav-drawer__utilities';
        panel.append(mobileUtilities);
      }
    }

    if (mobileUtilities && !mobileUtilities.querySelector('[data-wishlist-header-link]')) {
      const mobileLink = createHeaderWishlistLink('wishlist-mobile-link');
      mobileUtilities.append(mobileLink);
    }
  }

  function productHandleFromLocation() {
    const match = window.location.pathname.match(/(?:^|\/)products\/([^/?#]+)/i);
    return match ? normalizeHandle(match[1]) : null;
  }

  function createPdpWishlistButton(handle, title) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'wishlist-toggle wishlist-toggle--pdp';
    button.dataset.wishlistToggle = '';
    button.dataset.wishlistPdp = '';
    button.dataset.productHandle = handle;
    button.dataset.productTitle = title;
    button.setAttribute('aria-pressed', 'false');
    button.append(createHeartIcon());

    const label = document.createElement('span');
    label.dataset.wishlistLabel = '';
    label.textContent = text('addLabel');
    button.append(label);
    return button;
  }

  function ensurePdpWishlist(root = document) {
    const productPage = root.querySelector?.('[data-product-page]') || (root.matches?.('[data-product-page]') ? root : null);
    if (!productPage || productPage.querySelector('[data-wishlist-pdp]')) return;

    const handle = productHandleFromLocation();
    const form = productPage.querySelector('.product-form--ajax');
    if (!handle || !form) return;

    const title = (productPage.querySelector('.product-purchase__title')?.textContent || document.querySelector('h1')?.textContent || 'this product').trim();
    const button = createPdpWishlistButton(handle, title);
    form.insertAdjacentElement('afterend', button);
    updateControlState(button);
  }

  function formatMoney(cents, currency) {
    const numericValue = Number(cents);
    if (!Number.isFinite(numericValue)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'AED'
      }).format(numericValue / 100);
    } catch (error) {
      return `${currency || 'AED'} ${(numericValue / 100).toFixed(2)}`;
    }
  }

  function productUrl(handle) {
    return rootUrl(`products/${encodeURIComponent(handle)}`);
  }

  function imageSource(product) {
    const image = product.featured_image || (Array.isArray(product.images) ? product.images[0] : null);
    if (!image) return null;
    if (typeof image === 'string') return image;
    return image.src || null;
  }

  function currentVariant(product) {
    if (!Array.isArray(product.variants)) return null;
    return product.variants.find((variant) => variant.available) || product.variants[0] || null;
  }

  function createWishlistProductCard(product, handle, currency) {
    const article = document.createElement('article');
    article.className = 'wishlist-product-card';
    article.dataset.wishlistItem = handle;

    const mediaLink = document.createElement('a');
    mediaLink.className = 'wishlist-product-card__media';
    mediaLink.href = productUrl(handle);

    const imageUrl = imageSource(product);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      mediaLink.append(image);
    }
    article.append(mediaLink);

    const content = document.createElement('div');
    content.className = 'wishlist-product-card__content';

    const title = document.createElement('h2');
    title.className = 'wishlist-product-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = productUrl(handle);
    titleLink.textContent = product.title || handle;
    title.append(titleLink);
    content.append(title);

    const variant = currentVariant(product);
    const price = Number(variant?.price ?? product.price);
    const compareAt = Number(variant?.compare_at_price ?? product.compare_at_price);
    if (Number.isFinite(price)) {
      const priceRow = document.createElement('div');
      priceRow.className = 'wishlist-product-card__price';

      const currentPrice = document.createElement('span');
      currentPrice.className = compareAt > price ? 'wishlist-product-card__sale-price' : 'wishlist-product-card__current-price';
      currentPrice.textContent = formatMoney(price, currency);
      priceRow.append(currentPrice);

      if (Number.isFinite(compareAt) && compareAt > price) {
        const comparePrice = document.createElement('s');
        comparePrice.textContent = formatMoney(compareAt, currency);
        priceRow.append(comparePrice);
      }
      content.append(priceRow);
    }

    const availability = document.createElement('p');
    availability.className = `wishlist-product-card__availability${product.available ? ' is-available' : ''}`;
    availability.textContent = product.available ? text('inStock') : text('soldOut');
    content.append(availability);

    const actions = document.createElement('div');
    actions.className = 'wishlist-product-card__actions';

    if (product.available && Array.isArray(product.variants) && product.variants.length === 1 && variant?.available) {
      const form = document.createElement('form');
      form.className = 'product-card__form wishlist-product-card__form';
      form.method = 'post';
      form.action = rootUrl('cart/add');

      const id = document.createElement('input');
      id.type = 'hidden';
      id.name = 'id';
      id.value = String(variant.id);
      form.append(id);

      const quantity = document.createElement('input');
      quantity.type = 'hidden';
      quantity.name = 'quantity';
      quantity.value = '1';
      form.append(quantity);

      const addButton = document.createElement('button');
      addButton.type = 'submit';
      addButton.className = 'button wishlist-product-card__add';
      addButton.dataset.cartSubmit = '';
      addButton.textContent = text('addToCart');
      form.append(addButton);
      actions.append(form);
    } else {
      const viewLink = document.createElement('a');
      viewLink.className = 'button button--secondary wishlist-product-card__view';
      viewLink.href = productUrl(handle);
      viewLink.textContent = product.available ? text('chooseOptions') : text('viewProduct');
      actions.append(viewLink);
    }

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'wishlist-product-card__remove';
    removeButton.dataset.wishlistToggle = '';
    removeButton.dataset.productHandle = handle;
    removeButton.dataset.productTitle = product.title || handle;
    removeButton.setAttribute('aria-pressed', 'true');
    removeButton.textContent = text('removeLabel');
    actions.append(removeButton);

    content.append(actions);
    article.append(content);
    return article;
  }

  async function fetchSavedProduct(handle) {
    const response = await fetch(`${productUrl(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error(`Wishlist product request failed: ${response.status}`);
    const product = await response.json();
    if (!product || typeof product !== 'object') throw new Error('Wishlist product response was invalid');
    return { handle, product };
  }

  async function mapWithConcurrency(items, limit, worker) {
    const results = new Array(items.length);
    let nextIndex = 0;

    async function runWorker() {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        try {
          results[index] = await worker(items[index]);
        } catch (error) {
          results[index] = { handle: items[index], error };
        }
      }
    }

    const workers = Array.from({ length: Math.min(limit, items.length) }, runWorker);
    await Promise.all(workers);
    return results;
  }

  function setWishlistPageState(root, state) {
    const loading = root.querySelector('[data-wishlist-loading]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const grid = root.querySelector('[data-wishlist-grid]');

    if (loading) loading.hidden = state !== 'loading';
    if (empty) empty.hidden = state !== 'empty';
    if (grid) grid.hidden = state !== 'ready';
  }

  function updateWishlistPageCount(root, count) {
    const countNode = root.querySelector('[data-wishlist-count]');
    if (!countNode) return;
    countNode.textContent = countText(count);
  }

  async function renderWishlistPage() {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;

    const renderToken = ++wishlistPageRenderToken;
    const grid = root.querySelector('[data-wishlist-grid]');
    const unavailable = root.querySelector('[data-wishlist-unavailable]');
    const currency = root.dataset.currency || 'AED';

    if (!grid) return;
    grid.replaceChildren();
    if (unavailable) {
      unavailable.hidden = true;
      unavailable.textContent = '';
    }

    if (handles.length === 0) {
      updateWishlistPageCount(root, 0);
      setWishlistPageState(root, 'empty');
      return;
    }

    setWishlistPageState(root, 'loading');
    const savedHandles = [...handles];
    const results = await mapWithConcurrency(savedHandles, FETCH_CONCURRENCY, fetchSavedProduct);
    if (renderToken !== wishlistPageRenderToken) return;

    let validCount = 0;
    let invalidCount = 0;

    results.forEach((result) => {
      if (!result || result.error || !result.product) {
        invalidCount += 1;
        return;
      }

      grid.append(createWishlistProductCard(result.product, result.handle, currency));
      validCount += 1;
    });

    syncControls(grid);
    updateWishlistPageCount(root, validCount);

    if (invalidCount > 0 && unavailable) {
      unavailable.hidden = false;
      unavailable.textContent = invalidCount === 1
        ? text('oneUnavailable')
        : text('unavailableItems').replace('[count]', String(invalidCount));
    }

    setWishlistPageState(root, validCount > 0 ? 'ready' : 'empty');
  }

  function handleWishlistPageChange(event) {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;

    const changedHandle = normalizeHandle(event.detail?.handle || '');
    const action = event.detail?.action;
    if (changedHandle && action === 'remove') {
      root.querySelector(`[data-wishlist-item="${CSS.escape(changedHandle)}"]`)?.remove();
      const remainingCards = root.querySelectorAll('[data-wishlist-item]').length;
      updateWishlistPageCount(root, remainingCards);
      if (remainingCards === 0) setWishlistPageState(root, 'empty');
      return;
    }

    renderWishlistPage();
  }

  function initialize(root = document) {
    ensureHeaderLinks(root);
    ensurePdpWishlist(root);
    syncControls(root);
    if (root === document || root.querySelector?.('[data-wishlist-page]')) renderWishlistPage();
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-wishlist-toggle]');
    if (!control) return;
    event.preventDefault();
    event.stopPropagation();
    toggleWishlist(control);
  });

  document.addEventListener(CHANGE_EVENT, handleWishlistPageChange);

  window.addEventListener('storage', (event) => {
    if (event.key !== PRIMARY_STORAGE_KEY && event.key !== FALLBACK_STORAGE_KEY) return;
    handles = readStoredHandles();
    syncControls();
    renderWishlistPage();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  const observer = new MutationObserver((mutations) => {
    const relevantChange = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) return false;
      return node.matches('[data-wishlist-toggle], [data-product-page], [data-site-header]')
        || Boolean(node.querySelector('[data-wishlist-toggle], [data-product-page], [data-site-header]'));
    }));

    if (!relevantChange || mutationSyncQueued) return;
    mutationSyncQueued = true;
    window.requestAnimationFrame(() => {
      mutationSyncQueued = false;
      ensureHeaderLinks();
      ensurePdpWishlist();
      syncControls();
    });
  });

  function start() {
    initialize();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
